import React, { useEffect, useState } from "react";
import SearchBar from "../components/CommonComponents/SearchInput";
import Pagination from "../components/CommonComponents/Pagination";
import TableActionButtons from "../components/CommonComponents/TableActionButtons";
import { tableContainer, tableBase, tableHead, tableHeader, tableCell, tableRow } from "../utils/tableStyle";

// Modals
import AddCustomerModal from "../components/superadmin/AddCustomerModal";
import DeleteModal from "../components/CommonComponents/ConfirmationModal";

// Toast utils
import { showSuccessToast, showErrorToast } from "../utils/toast";
import { resolveAssetUrl } from "../utils/assetUrl";

// API calls (services layer)
import { fetchCustomers, updateCustomerStatus } from "../services/customerApi";

const CustomerList = () => {
  // ================= STATE =================
  const [customers, setCustomers] = useState([]); // Customer list
  const [selectedCustomer, setSelectedCustomer] = useState(null); // For edit/toggle
  const [selectedUserId, setSelectedUserId] = useState(null); // For delete

  // Modal state manager
  const [modal, setModal] = useState({
    addEdit: false,
    delete: false,
    toggle: false,
  });

  // Pagination + search
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const customersPerPage = 5;

  // ================= API CALLS =================
  const loadCustomers = async () => {
    try {
      const response = await fetchCustomers(); // call API service
      setCustomers(response.data); // save into state
    } catch (error) {
      console.error("Error fetching customers:", error);
      showErrorToast("Failed to fetch customer list.");
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await updateCustomerStatus(selectedUserId, "DELETED");

      // Optimistically update UI without full reload
      setCustomers((prev) =>
        prev.map((c) =>
          c.id === selectedUserId ? { ...c, status: "DELETED" } : c
        )
      );

      setModal((prev) => ({ ...prev, delete: false }));
      showSuccessToast("Customer marked as DELETED.");
    } catch (error) {
      console.error("Error deleting customer:", error);
      showErrorToast("Failed to delete customer.");
    }
  };

  const handleToggleStatus = async () => {
    try {
      const newStatus =
        selectedCustomer.status.toUpperCase() === "ACTIVE"
          ? "INACTIVE"
          : "ACTIVE";

      await updateCustomerStatus(selectedCustomer.id, newStatus);

      // Update only that customer in state
      setCustomers((prev) =>
        prev.map((c) =>
          c.id === selectedCustomer.id ? { ...c, status: newStatus } : c
        )
      );

      setModal((prev) => ({ ...prev, toggle: false }));
      showSuccessToast(`Customer status set to ${newStatus}.`);
    } catch (error) {
      console.error("Error toggling status:", error);
      showErrorToast("Failed to update status.");
    }
  };

  // ================= HANDLERS =================
  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setModal((prev) => ({ ...prev, addEdit: true }));
  };

  const handleAdd = () => {
    setSelectedCustomer(null); // new customer
    setModal((prev) => ({ ...prev, addEdit: true }));
  };

  const handleCloseModal = () => {
    setSelectedCustomer(null);
    setModal((prev) => ({ ...prev, addEdit: false }));
  };

  const handleUserAddedOrUpdated = () => {
    loadCustomers(); // reload after add/edit
    handleCloseModal();
  };

  // ================= FILTER & PAGINATION =================
  const normalizedSearch = String(searchTerm || "").toLowerCase();
  const filteredCustomers = customers.filter((customer) =>
    (customer.name || "").toLowerCase().includes(normalizedSearch)
  );

  const indexOfLast = currentPage * customersPerPage;
  const indexOfFirst = indexOfLast - customersPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirst, indexOfLast);
  const totalPages =
    Math.ceil(filteredCustomers.length / customersPerPage) || 1;


  // ================= LIFECYCLE =================
  useEffect(() => {
    loadCustomers();
  }, []);

  // ================= RENDER =================
  return (
    <div className="px-6 py-10 max-w-7xl mx-auto min-h-screen" style={{ background: 'var(--color-hover)' }}>
      {/* Search + Add Button */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <SearchBar
          placeholder="Search by name..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
        />

        <button
          onClick={handleAdd}
          className="btn-primary px-6 py-2 rounded-md font-medium shadow-md transition focus:outline-none"
        >
          + Add Customer
        </button>
      </div>

      {/* Title */}
      <h2 className="text-3xl font-bold mb-4" style={{ color: 'var(--color-text)' }}>Customers List</h2>

      {/* Table */}
      <div className={tableContainer}>
        <table className={tableBase}>
          <thead className={tableHead} style={{ background: 'var(--color-primary)' }}>
            <tr>
              <th className={tableHeader + ' text-left'}>#</th>
              <th className={tableHeader + ' text-left'}>Logo</th>
              <th className={tableHeader + ' text-left'}>Name</th>
              <th className={tableHeader + ' text-left'}>System Type</th>
              <th className={tableHeader + ' text-left'}>Status</th>
              <th className={tableHeader + ' text-left'}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentCustomers.map((customer, index) => (
              <tr
                key={customer.id}
                className={tableRow + ' border-b last:border-0' + (customer.status === "DELETED" ? " opacity-50" : "")}
              >
                <td className={tableCell}>{indexOfFirst + index + 1}</td>
                <td className={tableCell}>
                  {customer.logo ? (
                    <img
                      src={resolveAssetUrl(customer.logo)}
                      alt={`${customer.name} logo`}
                      className="w-10 h-10 object-contain rounded bg-white border"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className={tableCell + ' font-semibold'}>
                  {customer.name}
                </td>
                <td className={tableCell}>{customer.system_type}</td>
                <td className={tableCell}>
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${customer.status === "ACTIVE"
                      ? "bg-green-100 text-green-600"
                      : customer.status === "INACTIVE"
                        ? "bg-gray-200 text-gray-400"
                        : "bg-red-100 text-red-600"
                      }`}
                  >
                    {customer.status}
                  </span>
                </td>

                <td className={tableCell}>
                  <TableActionButtons
                    canEdit={customer.status !== "DELETED"}
                    canDelete={customer.status !== "DELETED"}
                    canToggle={customer.status !== "DELETED"}
                    onEdit={() => handleEdit(customer)}
                    onDelete={() => {
                      setSelectedUserId(customer.id);
                      setModal((prev) => ({ ...prev, delete: true }));
                    }}
                    onToggle={() => {
                      setSelectedCustomer(customer);
                      setModal((prev) => ({ ...prev, toggle: true }));
                    }}
                    toggleStatus={customer.status}
                  />
                </td>
              </tr>
            ))}
            {currentCustomers.length === 0 && (
              <tr>
                <td colSpan="6" className={tableCell + ' text-center py-8'}>
                  No customers found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      {/* Modals */}
      {modal.addEdit && (
        <AddCustomerModal
          editData={selectedCustomer}
          onClose={handleCloseModal}
          onUserAddedOrUpdated={handleUserAddedOrUpdated}
        />
      )}

      <DeleteModal
        isOpen={modal.delete}
        onClose={() => setModal((prev) => ({ ...prev, delete: false }))}
        onConfirm={handleConfirmDelete}
        title="Delete Customer"
        message={`Are you sure you want to delete "${customers.find((c) => c.id === selectedUserId)?.name
          }"?`}
        confirmText="Yes"
        cancelText="No"
        type="delete"
      />

      <DeleteModal
        isOpen={modal.toggle}
        onClose={() => setModal((prev) => ({ ...prev, toggle: false }))}
        onConfirm={handleToggleStatus}
        title="Change Status"
        message={`Are you sure you want to set this customer to ${selectedCustomer?.status === "ACTIVE" ? "INACTIVE" : "ACTIVE"
          }?`}
        confirmText="Yes"
        cancelText="No"
        type="delete"
      />
    </div>
  );
};

export default CustomerList;
